import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { projectsAPI, levantamentoAPI, contractorsAPI } from '@/lib/api-client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  ListChecks,
  Home,
  Calculator,
  ClipboardCheck,
  Users,
  HardHat,
  Play,
  Ruler,
  CheckCircle2,
  Check,
  ChevronRight,
  Route,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const GOLD = '#b8a378'

type StepState = 'done' | 'current' | 'future'

interface JourneyStep {
  id: number
  label: string
  description: string
  icon: LucideIcon
  phase: number
}

const STEPS: JourneyStep[] = [
  { id: 2, label: 'Definir unidades', description: 'Cadastrar plantas, blocos e unidades do projeto', icon: Home, phase: 1 },
  { id: 1, label: 'Definir atividades', description: 'Montar a estrutura de fases, etapas e atividades', icon: ListChecks, phase: 1 },
  { id: 3, label: 'Levantamento de materiais', description: 'Quantificar materiais e serviços por ambiente', icon: Calculator, phase: 2 },
  { id: 4, label: 'Revisar cobertura', description: 'Conferir se todas as atividades têm insumos vinculados', icon: ClipboardCheck, phase: 2 },
  { id: 5, label: 'Cadastrar fornecedores', description: 'Registrar fornecedores e criar pedidos de compra', icon: Users, phase: 3 },
  { id: 6, label: 'Cadastrar empreiteiros', description: 'Vincular empreiteiros ao projeto', icon: HardHat, phase: 4 },
  { id: 7, label: 'Iniciar execução', description: 'Mudar o status do projeto para Em Andamento', icon: Play, phase: 6 },
  { id: 8, label: 'Registrar medições', description: 'Aferir o avanço físico das atividades', icon: Ruler, phase: 7 },
  { id: 9, label: 'Encerrar projeto', description: 'Concluir formalmente o projeto', icon: CheckCircle2, phase: 8 },
]

interface ProjectJourneyCardProps {
  project: any
  stats: any
  projectId: string
  onTabChange: (tab: string) => void
}

export function ProjectJourneyCard({ project, stats, projectId, onTabChange }: ProjectJourneyCardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Additional queries for step checks
  const { data: levantamentoData } = useQuery({
    queryKey: ['levantamento-journey', projectId],
    queryFn: () => levantamentoAPI.getForProject(projectId),
    staleTime: 1000 * 60 * 5,
  })

  const { data: reviewSummary } = useQuery({
    queryKey: ['review-summary', projectId],
    queryFn: () => projectsAPI.getReviewSummary(projectId),
    staleTime: 1000 * 60 * 5,
    enabled: (project?._count?.activities ?? 0) > 0,
  })

  const { data: measurementsData } = useQuery({
    queryKey: ['measurements-journey', projectId],
    queryFn: () => projectsAPI.listMeasurements(projectId, { limit: 1 }),
    staleTime: 1000 * 60 * 5,
  })

  const { data: projectContractors } = useQuery({
    queryKey: ['contractors-by-project', projectId],
    queryFn: () => contractorsAPI.listByProject(projectId),
    staleTime: 1000 * 60 * 5,
  })

  // Mutation to start execution
  const startExecutionMutation = useMutation({
    mutationFn: () => projectsAPI.update(projectId, { status: 'IN_PROGRESS' }),
    onSuccess: () => {
      toast.success('Projeto iniciado!')
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project-stats', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  // Step completion checks
  function isStepDone(stepId: number): boolean {
    switch (stepId) {
      case 1: return (project?._count?.activities ?? 0) > 0
      case 2: return (project?._count?.units ?? 0) > 0
      case 3: {
        if (!levantamentoData) return false
        const totalItens = levantamentoData?.totalItens ?? levantamentoData?.itens?.length ?? 0
        return totalItens > 0
      }
      case 4: {
        if (!reviewSummary || (project?._count?.activities ?? 0) === 0) return false
        const activities = reviewSummary?.activities || reviewSummary
        if (Array.isArray(activities)) {
          return activities.length > 0 && activities.every((a: any) => (a.itemCount ?? a.coveredCount ?? 0) > 0)
        }
        return (reviewSummary?.uncoveredCount === 0 && (reviewSummary?.coveredCount ?? 0) > 0)
      }
      case 5: return (stats?.purchaseOrders ?? 0) > 0
      case 6: {
        const contractors = Array.isArray(projectContractors) ? projectContractors : projectContractors?.data
        return (contractors?.length ?? 0) > 0
      }
      case 7: return project?.status === 'IN_PROGRESS' || project?.status === 'COMPLETED'
      case 8: {
        const measurements = measurementsData?.data || measurementsData
        return Array.isArray(measurements) ? measurements.length > 0 : false
      }
      case 9: return project?.status === 'COMPLETED'
      default: return false
    }
  }

  // Handle step action
  function handleAction(stepId: number) {
    switch (stepId) {
      case 1: onTabChange('activities'); break
      case 2: onTabChange('units'); break
      case 3: navigate(`/projects/${projectId}/levantamento`); break
      case 4: navigate(`/projects/${projectId}/review-activities`); break
      case 5: navigate('/suppliers'); break
      case 6: navigate('/contractors'); break
      case 7: {
        if (project?.status === 'PLANNING') {
          if (confirm('Deseja iniciar a execução deste projeto?')) {
            startExecutionMutation.mutate()
          }
        }
        break
      }
      case 8: onTabChange('measurements'); break
      case 9: navigate(`/projects/${projectId}/close`); break
    }
  }

  // Calculate step states
  const firstIncompleteIdx = STEPS.findIndex(step => !isStepDone(step.id))

  function getStepState(index: number): StepState {
    if (isStepDone(STEPS[index].id)) return 'done'
    if (index === firstIncompleteIdx) return 'current'
    return 'future'
  }

  const completedCount = STEPS.filter(step => isStepDone(step.id)).length

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Route className="h-5 w-5" style={{ color: GOLD }} />
            <CardTitle className="text-base">Jornada do Projeto</CardTitle>
          </div>
          <Badge variant="outline" className="text-xs">
            {completedCount}/{STEPS.length} concluídos
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {STEPS.map((step, index) => {
            const state = getStepState(index)
            const isLast = index === STEPS.length - 1

            return (
              <div key={step.id} className="relative flex gap-4">
                {/* Vertical rail */}
                <div className="flex flex-col items-center">
                  {/* Node */}
                  <StepNode state={state} icon={step.icon} />
                  {/* Connector */}
                  {!isLast && <StepConnector done={state === 'done'} />}
                </div>

                {/* Content */}
                <div className={`flex-1 pb-5 ${isLast ? 'pb-0' : ''}`}>
                  <div className="flex items-start justify-between gap-2 -mt-0.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          state === 'done' ? 'text-neutral-500' :
                          state === 'current' ? 'text-neutral-900' :
                          'text-neutral-400'
                        }`}>
                          {step.label}
                        </span>
                        {state === 'done' && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-green-200 text-green-600 bg-green-50">
                            Concluído
                          </Badge>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${
                        state === 'current' ? 'text-neutral-500' : 'text-neutral-400'
                      }`}>
                        {step.description}
                      </p>
                    </div>

                    {/* Action */}
                    {state === 'current' && (
                      <Button
                        size="sm"
                        className="shrink-0 text-xs h-7"
                        style={{ background: `linear-gradient(135deg, ${GOLD}, #9a8a6a)` }}
                        onClick={() => handleAction(step.id)}
                        disabled={step.id === 7 && startExecutionMutation.isPending}
                      >
                        {step.id === 7 ? 'Iniciar' : 'Ir'}
                        <ChevronRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                    {state === 'done' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-xs h-7 text-neutral-400 hover:text-neutral-600"
                        onClick={() => handleAction(step.id)}
                      >
                        Ver
                        <ChevronRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function StepNode({ state, icon: Icon }: { state: StepState; icon: LucideIcon }) {
  if (state === 'done') {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ background: `linear-gradient(135deg, ${GOLD}, #9a8a6a)` }}
      >
        <Check className="h-4 w-4 text-white" />
      </div>
    )
  }

  if (state === 'current') {
    return (
      <div className="relative flex h-8 w-8 shrink-0 items-center justify-center">
        <div
          className="absolute inset-0 rounded-full animate-ping opacity-20"
          style={{ backgroundColor: GOLD }}
        />
        <div
          className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white"
          style={{ borderColor: GOLD }}
        >
          <Icon className="h-4 w-4" style={{ color: GOLD }} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-neutral-200 bg-neutral-50">
      <Icon className="h-4 w-4 text-neutral-300" />
    </div>
  )
}

function StepConnector({ done }: { done: boolean }) {
  return (
    <div
      className={`w-0.5 flex-1 min-h-[20px] ${done ? '' : 'border-l-2 border-dashed border-neutral-200 w-0'}`}
      style={done ? { background: `linear-gradient(to bottom, ${GOLD}, #9a8a6a)` } : undefined}
    />
  )
}
